// StationSelectLayout – výběr stanice: filtry vlevo + mapa OpenLayers + Panel1 (vybraná stanice) + virtualizovaná tabulka Panel3
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { List as VirtualList } from 'react-window'
import { Spinner } from '@heroui/react'

import { normalizeCzech, haversineKm } from '../utils/geo'
import { likeMatch, applyPlusMinusGhId } from '../utils/likeMatch'
import { parseGhIdExpr, evalGhIdExpr } from '../utils/ghIdExpr'
import { StationMap } from '../components/map/StationMap'
import { StationPanelSmall } from '../components/panels/StationPanelSmall'
import { StationPanelLine } from '../components/panels/StationPanelLine'
import { FiltersPanel } from '../components/filters/FiltersPanel'
import { useSavedGroupTrees, resolveGroupPaths } from '../hooks/useSavedGroupTrees'
import { buildGroupColorMapFromPaths } from '../utils/groupColors'
import { addLastStation } from '../hooks/useLastStations'
import { useStationStore } from '../store/stationStore'
import { useAllStations } from '../hooks/useAppData'
import { useTrackView } from '../hooks/useTrackProduct'
import { UserIcon } from '../components/auth/UserIcon'
import { SelectedStationBox } from '../components/panels/SelectedStationBox'
import groupIcon from '../assets/icons/group.svg'

// Row component for react-window v2 – must be defined outside the parent component
function StationRow({ index, style, stations, selectedGhId, onSelect }) {
  const station = stations[index]
  if (!station) return null
  return (
    <div style={style}>
      <StationPanelLine
        station={station}
        isSelected={selectedGhId === station.gh_id}
        onSelect={onSelect}
      />
    </div>
  )
}


// Apply all filters on client-side.
// groupGhIdSet — sjednocení gh_id vybraných uzlů filtru „Skupina" (null = filtr neaktivní),
// počítá se v komponentě přes resolveGroupPaths, protože applyFilters nemá přístup ke stromům.
function applyFilters(stations, filters, groupGhIdSet) {
  let result = stations

  // Skupina (stromový výběr podle uložených skupin)
  if (filters.groupPaths?.length > 0 && groupGhIdSet) {
    result = result.filter(s => groupGhIdSet.has(s.gh_id))
  }

  // GH_ID (prefix, LIKE vzor s % a _, nebo výraz s AND/OR/NOT — viz ghIdExpr.js).
  // Chybná syntaxe filtr nezapíná (výsledek zůstane beze změny), chyba se ukáže ve FiltersPanel.
  if (filters.ghId) {
    const { ast, error } = parseGhIdExpr(filters.ghId)
    if (!error && ast) result = result.filter(s => evalGhIdExpr(s.gh_id, ast))
  }

  // WSI
  if (filters.wsi) {
    result = result.filter(s => likeMatch(s.wsi, filters.wsi))
  }

  // Station type
  if (filters.stationTypes.length > 0) {
    result = result.filter(s => filters.stationTypes.includes(s.station_type))
  }

  // Elevation
  if (filters.elevationMin) {
    const min = parseFloat(filters.elevationMin)
    if (!isNaN(min)) result = result.filter(s => (s.elevation ?? 0) >= min)
  }
  if (filters.elevationMax) {
    const max = parseFloat(filters.elevationMax)
    if (!isNaN(max)) result = result.filter(s => (s.elevation ?? 0) <= max)
  }

  // Administrative hierarchy (paths)
  if (filters.hierarchyPaths.length > 0) {
    result = result.filter(s => {
      const stationPath = [s.h1, s.h2, s.h3, s.h4]
      return filters.hierarchyPaths.some(selectedPath =>
        selectedPath.every((val, idx) => val === stationPath[idx])
      )
    })
  }

  // Basin
  if (filters.basin.length > 0) {
    result = result.filter(s => s.basin && filters.basin.includes(s.basin))
  }

  // Vlastník stanice (geography.sow_name)
  if (filters.stationOwners?.length > 0) {
    result = result.filter(s => s.sow_name && filters.stationOwners.includes(s.sow_name))
  }

  // Element abbreviations – elAbbreviations je pole tuplů [obs_type, el_abbreviation]
  // elCurrentOnly: pokud je zatrženo, veličina se počítá jen když se měří doposud (observation.end_date > dnes).
  if (filters.elAbbreviations.length > 0) {
    const today = new Date().toISOString().split('T')[0]
    const matchesEl = (o, obsType, abbr) =>
      o.obs_type === obsType && o.el_abbreviation === abbr &&
      (!filters.elCurrentOnly || (o.end_date && o.end_date > today))

    if (filters.elMatchMode === 'all') {
      result = result.filter(s => {
        if (!s.observation) return false
        return filters.elAbbreviations.every(([obsType, abbr]) =>
          s.observation.some(o => matchesEl(o, obsType, abbr))
        )
      })
    } else {
      result = result.filter(s => {
        if (!s.observation) return false
        return filters.elAbbreviations.some(([obsType, abbr]) =>
          s.observation.some(o => matchesEl(o, obsType, abbr))
        )
      })
    }
  }

  // Active only (jen stanice s end_date > dnes)
  if (filters.activeOnly) {
    const today = new Date().toISOString().split('T')[0]
    result = result.filter(s => s.end_date && s.end_date > today)
  }

  // Name (diacritics-insensitive)
  if (filters.name) {
    const q = normalizeCzech(filters.name)
    result = result.filter(s => normalizeCzech(s.name).includes(q))
  }

  // Full name (diacritics-insensitive)
  if (filters.fullName) {
    const q = normalizeCzech(filters.fullName)
    result = result.filter(s => normalizeCzech(s.full_name).includes(q))
  }

  // Locality radius
  if (filters.locality && filters.localityRadius) {
    const radius = parseFloat(filters.localityRadius)
    if (!isNaN(radius) && radius > 0) {
      result = result.filter(s => {
        if (s.latitude == null || s.longitude == null) return false
        return haversineKm(filters.locality.lat, filters.locality.lon, s.latitude, s.longitude) <= radius
      })
    }
  }

  // OSM locality radius (Nominatim search result)
  if (filters.osmLocality && filters.osmLocalityRadius) {
    const radius = parseFloat(filters.osmLocalityRadius)
    if (!isNaN(radius) && radius > 0) {
      result = result.filter(s => {
        if (s.latitude == null || s.longitude == null) return false
        return haversineKm(filters.osmLocality.lat, filters.osmLocality.lon, s.latitude, s.longitude) <= radius
      })
    }
  }

  // +GH_ID / -GH_ID — vynucené zahrnutí/vyloučení, aplikuje se jako poslední krok bez ohledu na ostatní filtry
  result = applyPlusMinusGhId(result, stations, filters.plusGhId, filters.minusGhId)

  return result
}

export function StationSelectLayout() {
  const navigate = useNavigate()
  // Sledování přístupů (etapa K2) — mapa / výběr stanice je systémová část bez uzlu v registru.
  useTrackView('map', 'map-select')
  const location = useLocation()
  const preselectGhId = location.state?.preselectGhId ?? null
  const selectedStation = useStationStore((state) => state.selectedStation)
  const setSelectedStation = useStationStore((state) => state.setSelectedStation)
  const storedFilters = useStationStore((state) => state.stationSelectFilters)
  const setStoredFilters = useStationStore((state) => state.setStationSelectFilters)
  const uiSettings = useStationStore((state) => state.uiSettings)
  const setUiSettings = useStationStore((state) => state.setUiSettings)

  // Načti všechny stanice 1x
  const { allStations, isLoading: stationsLoading } = useAllStations()

  const [filters, setFilters] = useState(storedFilters)
  const [tableVisible, setTableVisible] = useState(uiSettings.tableVisible)
  const [tableHeight, setTableHeight] = useState(uiSettings.tableHeight ?? 260)
  const [localityPickMode, setLocalityPickMode] = useState(false)
  const tableContainerRef = useRef(null)
  const [tableWidth, setTableWidth] = useState(800)
  const splitterDragRef = useRef(null)

  // Uklád filtry do store když se změní
  useEffect(() => {
    setStoredFilters(filters)
  }, [filters, setStoredFilters])

  // Ulož UI settings do store
  useEffect(() => {
    setUiSettings({ tableVisible, tableHeight })
  }, [tableVisible, tableHeight, setUiSettings])

  // Splitter drag
  const handleSplitterMouseDown = useCallback((e) => {
    e.preventDefault()
    const startY = e.clientY
    const startHeight = tableHeight
    const onMouseMove = (e) => {
      const delta = startY - e.clientY
      setTableHeight(Math.max(80, Math.min(startHeight + delta, window.innerHeight - 150)))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [tableHeight])

  // Pre-select station passed from StationDetailLayout
  useEffect(() => {
    if (!preselectGhId || allStations.length === 0) return
    const station = allStations.find(s => s.gh_id === preselectGhId)
    if (station) handleSelectStation(station)
  }, [preselectGhId, allStations]) // eslint-disable-line

  // Filtr „Skupina" — stromy ze všech uložených skupin + resolvnuté gh_id vybraných uzlů.
  // Hook je líný: stromy se staví (a dotazy běží) jen když jsou reálně nějaké groupPaths vybrané;
  // samotné rozbalení panelu obsluhuje FiltersPanel vlastním líným voláním useSavedGroupTrees.
  const hasGroupPaths = (filters.groupPaths?.length || 0) > 0
  const savedGroupTrees = useSavedGroupTrees(hasGroupPaths)
  const groupGhIdSet = useMemo(
    () => resolveGroupPaths(savedGroupTrees, filters.groupPaths),
    [savedGroupTrees, filters.groupPaths]
  )

  // Barevný mód „Podle skupiny“ na mapě — vizualizace je vidět jen když má uživatel ve filtru
  // „Skupina“ vybraný aspoň jeden uzel (stejný princip jako groupColorMode ve StationGroupLayout).
  const { colorByGhId: groupColorMap, legend: groupLegend } = useMemo(
    () => buildGroupColorMapFromPaths(savedGroupTrees, filters.groupPaths),
    [savedGroupTrees, filters.groupPaths]
  )

  // Apply all filters on client-side
  const filteredStations = useMemo(
    () => applyFilters(allStations, filters, groupGhIdSet),
    [allStations, filters, groupGhIdSet]
  )

  // Handle map click for locality pick
  const handleMapClick = useCallback(({ lat, lon }) => {
    if (localityPickMode) {
      setFilters(f => ({ ...f, locality: { lat, lon }, localityRadius: f.localityRadius || '50' }))
      setLocalityPickMode(false)
    }
  }, [localityPickMode])

  const handleSelectStation = useCallback((station) => {
    setSelectedStation(station)
    addLastStation(station)
  }, [])

  // Observe table container width for FixedSizeList
  useEffect(() => {
    if (!tableContainerRef.current) return
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setTableWidth(entry.contentRect.width)
      }
    })
    observer.observe(tableContainerRef.current)
    return () => observer.disconnect()
  }, [])

  // Row props for react-window v2 (memoized to avoid re-renders)
  const rowProps = useMemo(() => ({
    stations: filteredStations,
    selectedGhId: selectedStation?.gh_id ?? null,
    onSelect: handleSelectStation,
  }), [filteredStations, selectedStation, handleSelectStation])

  return (
    <div className="flex h-dvh overflow-hidden bg-gray-50">

      {/* Left: Filters */}
      <aside className="w-52 shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
        <div className="border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
            <button
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors flex-1 min-w-0"
              onClick={() => navigate('/')}
            >
              <span className="text-xl leading-none">🏠</span> Domů
            </button>
            <button
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors shrink-0"
              title="Skupina stanic"
              onClick={() => navigate('/station-group')}
            >
              <img src={groupIcon} className="w-6 h-6" alt="Skupina stanic" />
            </button>
            <UserIcon variant="compact" />
          </div>
          <SelectedStationBox station={selectedStation} />
        </div>
        <div className="flex-1 min-h-0">
          <FiltersPanel
            filters={filters}
            onChange={setFilters}
            onLocalityPickMode={setLocalityPickMode}
            localityPickMode={localityPickMode}
            showGroupFilter
          />
        </div>
      </aside>

      {/* Right: Map + table */}
      <div className="flex-1 flex flex-col overflow-hidden relative">

        {/* Map area */}
        <div className="flex-1 relative overflow-hidden">
          <StationMap
            stations={filteredStations}
            selectedStation={selectedStation}
            onStationSelect={handleSelectStation}
            onMapClick={handleMapClick}
            localityPickMode={localityPickMode}
            groupVizAvailable={hasGroupPaths}
            groupColorMap={groupColorMap}
            groupLegend={groupLegend}
          />

          {/* Loading banner */}
          {stationsLoading && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white/95 shadow-md rounded-xl px-3 py-1.5 text-xs text-gray-600 border border-gray-200">
              <Spinner size="sm" />
              Načítání stanic…
            </div>
          )}

          {/* Panel1 – selected station, top-right */}
          {selectedStation && (
            <div className="absolute top-2 right-2 z-10 w-[200px]">
              <StationPanelSmall
                station={selectedStation}
                onDetailClick={(s) => navigate(`/station/${s.gh_id}`)}
                onClose={() => setSelectedStation(null)}
              />
            </div>
          )}
        </div>

        {/* Table: resizable bottom panel */}
        {tableVisible ? (
          <>
            {/* Splitter */}
            <div
              className="h-1.5 bg-gray-200 hover:bg-blue-400 cursor-row-resize shrink-0 transition-colors"
              onMouseDown={handleSplitterMouseDown}
              ref={splitterDragRef}
            />
          <div className="border-gray-200 bg-white flex flex-col overflow-hidden shrink-0" style={{ height: tableHeight }}>
            {/* Table header */}
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50 shrink-0">
              <span className="text-xs text-gray-500">
                {allStations.length === 0 ? 'Načítám…' : `Zobrazeno ${filteredStations.length} stanic`}
              </span>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="hidden sm:block">
                  gh_id · název · typ · souřadnice · výška · pozorování
                </span>
                <button
                  title="Minimalizovat tabulku"
                  className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                  onClick={() => setTableVisible(false)}
                >
                  ▼
                </button>
              </div>
            </div>

            {/* Table column headers */}
            <div className="flex items-center gap-2 px-3 py-1 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-400 shrink-0">
              <span className="w-[90px] shrink-0">GH_ID</span>
              <span className="flex-1">Název</span>
              <span className="w-[50px] text-center shrink-0">Typ</span>
              <span className="w-[170px] shrink-0 hidden md:block">Souřadnice</span>
              <span className="w-[60px] text-right shrink-0">Výška</span>
              <span className="w-[200px] shrink-0 hidden lg:block">Pozorování</span>
              <span className="w-[60px] shrink-0" />
            </div>

            {/* Virtualized list */}
            <div className="flex-1 overflow-hidden" ref={tableContainerRef}>
              {filteredStations.length > 0 ? (
                <VirtualList
                  rowComponent={StationRow}
                  rowCount={filteredStations.length}
                  rowHeight={34}
                  rowProps={rowProps}
                  height={tableContainerRef.current?.clientHeight || 200}
                  width={tableWidth}
                />
              ) : (
                allStations.length > 0 && (
                  <div className="flex items-center justify-center h-full text-xs text-gray-400">
                    Žádné stanice neodpovídají zadaným kritériím
                  </div>
                )
              )}
            </div>
          </div>
          </>
        ) : (
          /* Table collapsed – show icon */
          <button
            className="absolute bottom-4 right-1/2 translate-x-1/2 z-10 bg-white shadow-lg border border-gray-200 rounded-full px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            onClick={() => setTableVisible(true)}
          >
            📋 Tabulka ({filteredStations.length})
          </button>
        )}
      </div>
    </div>
  )
}
